// Parts of the four writing steps (redesign slice 7b; wireframes 18 and 21):
// labelled fields, step 1 (Basics) and step 4 (Details). Steps 2
// and 3 are IngredientsStep and StepsStep; the review screen is
// RecipeReview. The page that holds them is RecipeWriterPage.
import type { ReactNode } from "react";
import { COLORS, COOKING_METHODS, DIET_TAGS, DIET_TAG_LABELS, FONT_SIZE, MEAL_TYPES, RECIPE_TYPES, RECIPE_TYPE_LABELS, SEASONS, SPACE } from "../../lib/constants";
import type { Recipe } from "../../lib/types";
import { smallLabel, supporting } from "../recipeStyles";
import { Chip } from "./LibraryFilters";
import { fieldInput, isYieldType, stepHeading } from "./writeStyles";

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: SPACE.xs }}>
      <span style={{ ...smallLabel, color: COLORS.soft }}>{label}</span>
      {children}
      {hint && <span style={supporting}>{hint}</span>}
    </label>
  );
}

export function ChipGroup({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: SPACE.sm }}>
      <legend style={{ ...smallLabel, color: COLORS.soft, padding: 0, marginBottom: SPACE.sm }}>{label}</legend>
      <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm }}>{children}</div>
      {hint && <span style={supporting}>{hint}</span>}
    </fieldset>
  );
}

// A whole number from a number field; empty counts as 0.
const whole = (v: string) => Math.max(0, Math.round(Number(v) || 0));
const toggle = (list: string[] = [], value: string) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type Change = (patch: Partial<Recipe>) => void;

// Step 1: name, dish type, meals, serves and times. Serves and times drive
// scaling, the grocery list and the day's time budget, so they come first.
export function BasicsStep({ recipe, onChange }: { recipe: Recipe; onChange: Change }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xxl }}>
      <h1 style={stepHeading}>The basics</h1>
      <Field label="Recipe name">
        <input autoFocus value={recipe.title} onChange={(e) => onChange({ title: e.target.value })} style={fieldInput} />
      </Field>
      <ChipGroup label="What kind of dish is it?" hint="This decides which plan slots it can fill.">
        {RECIPE_TYPES.map((t) => (
          <Chip key={t} on={recipe.recipeType === t} onClick={() => onChange({ recipeType: t })}>
            {(RECIPE_TYPE_LABELS as Record<string, string>)[t]}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup label="Which meals does it suit?">
        {MEAL_TYPES.map((m) => (
          <Chip key={m} on={(recipe.mealTypes || []).includes(m)} onClick={() => onChange({ mealTypes: toggle(recipe.mealTypes, m) })}>
            {cap(m)}
          </Chip>
        ))}
      </ChipGroup>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: SPACE.md }}>
        <Field label="Serves">
          <input type="number" min="1" inputMode="numeric" value={recipe.servings || ""} onChange={(e) => onChange({ servings: whole(e.target.value) })} style={fieldInput} />
        </Field>
        <Field label="Active min">
          <input type="number" min="0" inputMode="numeric" value={recipe.activeTime || ""} placeholder="0" onChange={(e) => onChange({ activeTime: whole(e.target.value) })} style={fieldInput} />
        </Field>
        <Field label="Cook min">
          <input type="number" min="0" inputMode="numeric" value={recipe.cookTime || ""} placeholder="0" onChange={(e) => onChange({ cookTime: whole(e.target.value) })} style={fieldInput} />
        </Field>
      </div>
      {isYieldType(recipe) && (
        <Field label="Makes" hint="Preserves and medicinal recipes give what they make instead of servings.">
          <input value={recipe.yield || ""} placeholder="4 half-pint jars" onChange={(e) => onChange({ yield: e.target.value })} style={fieldInput} />
        </Field>
      )}
      <p style={{ ...supporting, margin: 0 }}>Serves and times drive scaling, the grocery list and the day&apos;s time budget, so they are asked first. Everything else can wait for step 4.</p>
    </div>
  );
}

// Step 4: all optional. Who can see it is set in the share sheet on the
// recipe page (slice 8a), not here.
export function DetailsStep({ recipe, onChange, cuisines }: { recipe: Recipe; onChange: Change; cuisines: string[] }) {
  const num = (v: string) => Math.max(0, Number(v) || 0);
  const nutrition: [keyof Recipe, string][] = [
    ["caloriesPerServing", "Calories"],
    ["proteinPerServing", "Protein g"],
    ["carbsPerServing", "Carbs g"],
    ["fatPerServing", "Fat g"],
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xxl }}>
      <div>
        <h1 style={stepHeading}>Details</h1>
        <p style={{ ...supporting, margin: "var(--space-xs) 0 0" }}>All optional. They make the recipe easier to find and plan with.</p>
      </div>
      <Field label="Source" hint="A book, a person, a site's name.">
        <input value={recipe.source || ""} onChange={(e) => onChange({ source: e.target.value })} style={fieldInput} />
      </Field>
      <Field label="Source link">
        <input type="url" inputMode="url" value={recipe.sourceUrl || ""} placeholder="https://" onChange={(e) => onChange({ sourceUrl: e.target.value.trim() })} style={fieldInput} />
      </Field>
      <Field label="Cuisine">
        <input list="writer-cuisines" value={recipe.cuisine || ""} onChange={(e) => onChange({ cuisine: e.target.value })} style={fieldInput} />
        <datalist id="writer-cuisines">
          {cuisines.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      </Field>
      <ChipGroup label="Seasons it suits">
        {SEASONS.map((s) => (
          <Chip key={s} on={(recipe.seasons || []).includes(s)} onClick={() => onChange({ seasons: toggle(recipe.seasons, s) })}>
            {s}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup label="Diet">
        {DIET_TAGS.map((d) => (
          <Chip key={d} on={(recipe.dietTags || []).includes(d)} onClick={() => onChange({ dietTags: toggle(recipe.dietTags, d) })}>
            {(DIET_TAG_LABELS as Record<string, string>)[d]}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup label="Cooking method">
        {COOKING_METHODS.map((m) => (
          <Chip key={m} on={(recipe.cookingMethod || []).includes(m)} onClick={() => onChange({ cookingMethod: toggle(recipe.cookingMethod, m) })}>
            {m}
          </Chip>
        ))}
      </ChipGroup>
      <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
        <legend style={{ ...smallLabel, color: COLORS.soft, padding: 0, marginBottom: SPACE.sm }}>Nutrition per serving</legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: SPACE.md }}>
          {nutrition.map(([key, label]) => (
            <label key={key} style={{ display: "flex", flexDirection: "column", gap: SPACE.xs, fontSize: FONT_SIZE.sm, color: COLORS.soft }}>
              {label}
              <input type="number" min="0" inputMode="decimal" value={(recipe[key] as number) || ""} placeholder="0" onChange={(e) => onChange({ [key]: num(e.target.value) })} style={fieldInput} />
            </label>
          ))}
        </div>
        <span style={supporting}>Feeds the daily nutrition status on the plan.</span>
      </fieldset>
      <p style={{ ...supporting, margin: 0 }}>Private to you. Your household can see it if you&apos;ve turned that on. Share it, or make it public, from the recipe page after saving.</p>
    </div>
  );
}
